import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AutoStories as StoriesIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { solutionStoriesApi, SolutionStory } from '../services/api';
import { SolutionStoryDetailModal } from '../components/SolutionStoryDetailModal';
import { StoryCard } from '../components/solution-stories/StoryCard';
import { DirectApprovalPanel } from '../components/solution-stories/DirectApprovalPanel';
import { OptionsManagerPanel } from '../components/solution-stories/OptionsManagerPanel';
import { STORY_UI } from '../components/solution-stories/storyUiTokens';

type AdminTab = 'pending' | 'mine' | 'all' | 'direct' | 'options';

const SolutionStoryAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<AdminTab>('pending');
  const [detailStory, setDetailStory] = useState<SolutionStory | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [allSearch, setAllSearch] = useState('');
  const [allReviewStatus, setAllReviewStatus] = useState('');
  const [allIndustry, setAllIndustry] = useState('');
  const [allRegion, setAllRegion] = useState('');
  const [allPractice, setAllPractice] = useState('');

  const accessQuery = useQuery({
    queryKey: ['solution-stories-admin-access'],
    queryFn: () => solutionStoriesApi.getAdminAccess(),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (accessQuery.isError) {
      navigate('/solution-story', { replace: true });
      return;
    }
    if (accessQuery.isSuccess && !accessQuery.data.allowed) {
      navigate('/solution-story', { replace: true });
    }
  }, [accessQuery.isSuccess, accessQuery.isError, accessQuery.data?.allowed, navigate]);

  const allowed = !!accessQuery.data?.allowed;

  const pendingQuery = useQuery({
    queryKey: ['solution-stories-admin-pending'],
    queryFn: () => solutionStoriesApi.listAdminPending(),
    staleTime: 60 * 1000,
    enabled: allowed,
  });

  const mineQuery = useQuery({
    queryKey: ['solution-stories-admin-mine'],
    queryFn: () => solutionStoriesApi.listAdminMine(),
    staleTime: 60 * 1000,
    enabled: allowed,
  });

  const allQuery = useQuery({
    queryKey: ['solution-stories-admin-all'],
    queryFn: () => solutionStoriesApi.listAdminAll(),
    staleTime: 60 * 1000,
    enabled: allowed,
  });

  const activeQuery =
    tab === 'pending' ? pendingQuery : tab === 'mine' ? mineQuery : tab === 'all' ? allQuery : null;

  const allStoriesRaw: SolutionStory[] = allQuery.data?.stories ?? [];

  const allFilterOptions = useMemo(() => {
    const uniq = (values: (string | null | undefined)[]) =>
      Array.from(new Set(values.filter((v): v is string => !!v && v.trim().length > 0))).sort();
    return {
      reviewStatuses: uniq(allStoriesRaw.map((s) => s.review_status)),
      industries: uniq(allStoriesRaw.map((s) => s.industry)),
      regions: uniq(allStoriesRaw.map((s) => s.region)),
      practices: uniq(allStoriesRaw.map((s) => s.practice)),
    };
  }, [allStoriesRaw]);

  const filteredAllStories = useMemo(() => {
    const q = allSearch.trim().toLowerCase();
    return allStoriesRaw.filter((s) => {
      if (allReviewStatus && s.review_status !== allReviewStatus) return false;
      if (allIndustry && s.industry !== allIndustry) return false;
      if (allRegion && s.region !== allRegion) return false;
      if (allPractice && s.practice !== allPractice) return false;
      if (!q) return true;
      return [s.solution_provided_title, s.account_name, s.pr_code, s.reviewer]
        .filter(Boolean)
        .some((field) => (field as string).toLowerCase().includes(q));
    });
  }, [allStoriesRaw, allSearch, allReviewStatus, allIndustry, allRegion, allPractice]);

  const hasAllFilters =
    !!allSearch.trim() || !!allReviewStatus || !!allIndustry || !!allRegion || !!allPractice;

  const stories: SolutionStory[] =
    tab === 'all' ? filteredAllStories : activeQuery?.data?.stories ?? [];
  const detailIndex = detailStory
    ? stories.findIndex((s) => s.solution_story_id === detailStory.solution_story_id)
    : -1;

  const openDetail = useCallback(
    (id: string) => {
      const story = stories.find((s) => s.solution_story_id === id);
      if (!story) return;
      setDetailStory(story);
      setDetailOpen(true);
    },
    [stories]
  );

  const subtitle = useMemo(() => {
    if (tab === 'pending') {
      return 'Stories assigned to you and waiting for review.';
    }
    if (tab === 'all') {
      return 'Every solution story. Edit or approve any of them — saving reassigns you as the reviewer.';
    }
    if (tab === 'direct') {
      return 'Create an approved solution story directly from a PR code in projects tracker.';
    }
    if (tab === 'options') {
      return 'Manage practice, industry, and platform dropdown options and their icons.';
    }
    return 'All solution stories where you are tagged as the reviewer.';
  }, [tab]);

  const refetchLists = () => {
    queryClient.invalidateQueries({ queryKey: ['solution-stories-admin-pending'] });
    queryClient.invalidateQueries({ queryKey: ['solution-stories-admin-mine'] });
    queryClient.invalidateQueries({ queryKey: ['solution-stories-admin-all'] });
  };

  if (accessQuery.isLoading || !allowed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: STORY_UI.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mx: { xs: -2, sm: -3, md: -4 },
        mt: { xs: -1, sm: -1.5 },
        mb: -4,
        fontFamily: STORY_UI.fontSans,
        color: STORY_UI.ink,
        backgroundColor: STORY_UI.bg,
        minHeight: '100%',
        pb: '80px',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          pt: { xs: 2, sm: 3 },
          pb: 2,
          textAlign: 'center',
          backgroundColor: STORY_UI.heroBg,
          borderBottom: `1px solid ${STORY_UI.line}`,
          position: 'relative',
        }}
      >
        <Button
          id="admin-back-to-catalog-btn"
          startIcon={<ArrowBackIcon sx={{ fontSize: 16, color: '#fff' }} />}
          onClick={() => navigate('/solution-story')}
          sx={{
            position: { xs: 'static', sm: 'absolute' },
            left: { sm: 48 },
            top: { sm: 28 },
            mb: { xs: 1.5, sm: 0 },
            textTransform: 'none',
            borderRadius: STORY_UI.radiusSm,
            fontWeight: 600,
            fontSize: '13px',
            fontFamily: STORY_UI.fontSans,
            px: 2.5,
            py: 1,
            backgroundColor: STORY_UI.accent,
            color: '#fff',
            border: 'none',
            boxShadow: '0 4px 14px -4px rgba(0,100,255,0.45)',
            '&:hover': {
              backgroundColor: STORY_UI.accentDeep,
              color: '#fff',
            },
            '& .MuiButton-startIcon': { color: '#fff' },
          }}
        >
          Back to Stories
        </Button>
        <Typography
          component="h1"
          sx={{
            fontFamily: STORY_UI.fontDisplay,
            fontWeight: 600,
            fontSize: { xs: '28px', sm: '36px' },
            letterSpacing: '-0.02em',
            mb: '10px',
            color: STORY_UI.ink,
          }}
        >
          Solution Stories Admin
        </Typography>
        <Typography
          sx={{
            fontSize: '15.5px',
            color: STORY_UI.inkSoft,
            maxWidth: 700,
            mx: 'auto',
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, sm: 4 }, pt: 3, pb: 4 }}>
        <Tabs
          value={tab}
          onChange={(_e, v: string) => {
            if (v === 'pending' || v === 'mine' || v === 'all' || v === 'direct' || v === 'options') setTab(v);
          }}
          sx={{
            mb: 3,
            borderBottom: `1px solid ${STORY_UI.line}`,
            minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: STORY_UI.fontSans,
              fontWeight: 600,
              fontSize: '14px',
              color: STORY_UI.inkSoft,
              minHeight: 44,
            },
            '& .Mui-selected': { color: `${STORY_UI.accent} !important` },
            '& .MuiTabs-indicator': { backgroundColor: STORY_UI.accent, height: 3 },
          }}
        >
          <Tab
            id="admin-tab-pending"
            label={`Pending Review (${pendingQuery.data?.total_count ?? 0})`}
            value="pending"
          />
          <Tab
            id="admin-tab-mine"
            label={`My Reviews (${mineQuery.data?.total_count ?? 0})`}
            value="mine"
          />
          <Tab
            id="admin-tab-all"
            label={`All Solution Stories (${allQuery.data?.total_count ?? 0})`}
            value="all"
          />
          <Tab id="admin-tab-direct" label="Submit Solution Story" value="direct" />
          <Tab id="admin-tab-options" label="Configurations" value="options" />
        </Tabs>

        {tab === 'options' ? (
          <OptionsManagerPanel />
        ) : tab === 'direct' ? (
          <DirectApprovalPanel />
        ) : (
          <>
          {tab === 'all' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              <TextField
                id="admin-all-search"
                placeholder="Search by title, account, PR code, reviewer…"
                value={allSearch}
                onChange={(e) => setAllSearch(e.target.value)}
                size="small"
                sx={{ flex: '1 1 260px', minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: STORY_UI.inkFaint }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="admin-all-status-label">Status</InputLabel>
                <Select
                  labelId="admin-all-status-label"
                  label="Status"
                  value={allReviewStatus}
                  onChange={(e) => setAllReviewStatus(e.target.value)}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {allFilterOptions.reviewStatuses.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="admin-all-industry-label">Industry</InputLabel>
                <Select
                  labelId="admin-all-industry-label"
                  label="Industry"
                  value={allIndustry}
                  onChange={(e) => setAllIndustry(e.target.value)}
                >
                  <MenuItem value="">All industries</MenuItem>
                  {allFilterOptions.industries.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="admin-all-region-label">Region</InputLabel>
                <Select
                  labelId="admin-all-region-label"
                  label="Region"
                  value={allRegion}
                  onChange={(e) => setAllRegion(e.target.value)}
                >
                  <MenuItem value="">All regions</MenuItem>
                  {allFilterOptions.regions.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="admin-all-practice-label">Practice</InputLabel>
                <Select
                  labelId="admin-all-practice-label"
                  label="Practice"
                  value={allPractice}
                  onChange={(e) => setAllPractice(e.target.value)}
                >
                  <MenuItem value="">All practices</MenuItem>
                  {allFilterOptions.practices.map((v) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasAllFilters && (
                <Button
                  id="admin-all-clear-filters-btn"
                  onClick={() => {
                    setAllSearch('');
                    setAllReviewStatus('');
                    setAllIndustry('');
                    setAllRegion('');
                    setAllPractice('');
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: STORY_UI.inkSoft,
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Box>
          )}
          <>
        {!activeQuery?.isLoading && !activeQuery?.error && (
          <Typography sx={{ fontSize: '13px', color: STORY_UI.inkFaint, mb: 2 }}>
            <Box component="b" sx={{ color: STORY_UI.ink, fontWeight: 600 }}>
              {stories.length}
            </Box>{' '}
            {stories.length === 1 ? 'story' : 'stories'}
            {tab === 'all' && hasAllFilters && ` (of ${allStoriesRaw.length} total)`}
          </Typography>
        )}

        {activeQuery?.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={36} sx={{ color: STORY_UI.accent }} />
          </Box>
        ) : activeQuery?.error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Failed to load stories. Please try again.
          </Alert>
        ) : stories.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 12,
              gap: 2,
            }}
          >
            <StoriesIcon sx={{ fontSize: 56, color: STORY_UI.line }} />
            <Typography sx={{ color: STORY_UI.inkFaint, fontWeight: 600, fontSize: '1.1rem' }}>
              No stories found
            </Typography>
            <Typography sx={{ color: STORY_UI.inkFaint, fontSize: '13.5px' }}>
              {tab === 'pending'
                ? 'Nothing is waiting for your review right now.'
                : tab === 'all'
                ? 'No solution stories exist yet.'
                : 'No stories are assigned to you as reviewer.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {stories.map((story) => (
              <StoryCard
                key={story.solution_story_id}
                story={story}
                showSelect={false}
                onCardClick={openDetail}
              />
            ))}
          </Box>
        )}
          </>
          </>
        )}
      </Box>

      <SolutionStoryDetailModal
        open={detailOpen}
        mode="review"
        onClose={() => {
          setDetailOpen(false);
          setDetailStory(null);
        }}
        story={detailStory}
        stories={stories}
        currentIndex={detailIndex}
        onNavigate={(idx) => {
          if (idx >= 0 && idx < stories.length) {
            setDetailStory(stories[idx]);
          }
        }}
        onSaved={(updated) => {
          setDetailStory(updated);
          refetchLists();
        }}
        onApproved={() => {
          refetchLists();
        }}
        onArchived={() => {
          refetchLists();
        }}
      />
    </Box>
  );
};

export default SolutionStoryAdminPage;
